/* Support desk behaviour (design template templates/support/SupportPage.jsx, SpDesk + SpAnswer + SpContact).
   Every answer is already in the HTML (sections/oricle-support-desk.liquid renders the topic x model x variant grid), so this
   script only decides which one shows. The script holds no copy: labels and the screen reader lines are data attributes.

   · Two radiogroups (your hearing aid, your question) with the WAI roving tabindex: arrows, Home and End move and choose.
   · ?model= and ?topic= seed the picks on load; a #anchor does too (the topic's own id, or an alias the section lists, so the
     retired help pages can redirect to #track, #returns and so on). #contact is left to the browser. A pick rewrites the query
     with history.replaceState (never a navigation), so the address always names what is on screen.
   · A #faq-<question> anchor (the search sheet's answer links, Jackson 10/6) names one row of the questions list: the desk shows
     that list and leaves the anchor in the address; the FAQ accordion (assets/oricle.js) opens, scrolls to and focuses the row.
   · On a phone, choosing a question scrolls to its answer and "Change question" scrolls back to the picks.
   · A setup video swaps to the no-cookie player on a press (captions on, nothing before the press) and goes back to its still
     when its answer is hidden, so a video never plays on behind another answer.
   · Contact form (oricle-support-contact): a Zendesk anonymous request when the section names a subdomain, else the visitor's
     email app, pre-addressed. The form never claims "sent" for an email it only opened.
   Re-initialises on shopify:section:load because a custom element reconnects when the editor re-renders the section. */
(() => {
  'use strict';

  const MOBILE = '(max-width: 767px)';
  const prefersReduced = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const isMobile = () => !!(window.matchMedia && window.matchMedia(MOBILE).matches);
  const fillTokens = (s, v) => String(s || '').replace(/\{(\w+)\}/g, (m, k) => (v && v[k] != null ? v[k] : m));
  const player = (id) => `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&cc_load_policy=1&rel=0`;

  /* "track:order, returns:return" → { track: 'order', returns: 'return' } */
  const parseAliases = (str) => {
    const o = {};
    String(str || '').split(',').forEach((p) => {
      const i = p.indexOf(':');
      if (i > 0) { const k = p.slice(0, i).trim(); const v = p.slice(i + 1).trim(); if (k && v) o[k] = v; }
    });
    return o;
  };

  if (!customElements.get('oricle-support-desk')) {
    class OricleSupportDesk extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.models = [...this.querySelectorAll('[data-sd-model]')];
        this.topics = [...this.querySelectorAll('[data-sd-topic]')];
        this.panels = [...this.querySelectorAll('[data-sd-panel]')];
        this.live = this.querySelector('[data-sd-live]');
        this.aliases = parseAliases(this.dataset.aliases);
        this.variants = {};
        this.state = { model: this.dataset.model || '', topic: this.dataset.topic || '' };
        this.fromUrl();
        this.render(false);

        this.addEventListener('click', (e) => this.onClick(e));
        this.addEventListener('keydown', (e) => this.onKey(e));
        this.hashListener = () => this.onHash();
        window.addEventListener('hashchange', this.hashListener);
      }

      disconnectedCallback() {
        if (this.hashListener) window.removeEventListener('hashchange', this.hashListener);
      }

      /* The topic a #anchor names: a topic's own id, an alias the section lists, or the topic whose panel holds the row the
         anchor names (#faq-<question>). Anything else (#contact) is the browser's. */
      topicFromHash() {
        let h = '';
        try { h = decodeURIComponent((window.location.hash || '').slice(1)); } catch (err) { return ''; }
        const hasTopic = (id) => this.topics.some((x) => x.dataset.sdTopic === id);
        if (hasTopic(h)) return h;
        if (this.aliases[h] && hasTopic(this.aliases[h])) return this.aliases[h];
        const row = this.rowFromHash();
        return row ? row.closest('[data-sd-panel]').dataset.topic : '';
      }

      /* The questions-list row a #faq-<question> anchor names, if it is in this desk. */
      rowFromHash() {
        let h = '';
        try { h = decodeURIComponent((window.location.hash || '').slice(1)); } catch (err) { return null; }
        const row = h ? document.getElementById(h) : null;
        return row && this.contains(row) && row.closest('[data-sd-panel]') ? row : null;
      }

      /* A link on this page to #returns changes the hash and nothing else: choose that answer and take the visitor to it.
         A link to one question shows the questions list and keeps its anchor; the accordion takes the visitor to the row. */
      onHash() {
        const t = this.topicFromHash();
        if (!t) return;
        const row = this.rowFromHash();
        if (t !== this.state.topic) { this.state.topic = t; this.render(!row); if (row) this.url(true); }
        if (!row) this.scrollTo('picks');
      }

      /* ?model= / ?topic= first, then the hash. Only ids that exist are taken. */
      fromUrl() {
        let q; try { q = new URLSearchParams(window.location.search); } catch (err) { return; }
        const hasModel = (id) => this.models.some((x) => x.dataset.sdModel === id);
        const hasTopic = (id) => this.topics.some((x) => x.dataset.sdTopic === id);
        const m = q.get('model');
        if (m && hasModel(m)) this.state.model = m;
        let t = q.get('topic');
        if (!(t && hasTopic(t))) t = this.topicFromHash();
        if (t) this.state.topic = t;
      }

      onClick(e) {
        const model = e.target.closest('[data-sd-model]');
        if (model) { this.pick('model', model.dataset.sdModel); return; }
        const topic = e.target.closest('[data-sd-topic]');
        if (topic) { this.pick('topic', topic.dataset.sdTopic); return; }
        const variant = e.target.closest('[data-sd-variant]');
        if (variant) { this.variants[this.state.topic] = variant.dataset.sdVariant; this.render(true); return; }
        if (e.target.closest('[data-sd-back]')) { this.scrollTo('picks'); return; }
        const play = e.target.closest('[data-o-vf-play]');
        if (play) this.play(play);
      }

      /* WAI radio group: arrows and Home/End move the choice, and the choice follows focus. */
      onKey(e) {
        const radio = e.target.closest('[role="radio"]');
        if (!radio || !radio.closest('[role="radiogroup"]')) return;
        const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1, Home: 'first', End: 'last' };
        if (!(e.key in keys)) return;
        e.preventDefault();
        const group = [...radio.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]')];
        const i = group.indexOf(radio);
        const step = keys[e.key];
        const next = step === 'first' ? group[0] : step === 'last' ? group[group.length - 1] : group[(i + step + group.length) % group.length];
        next.focus();
        next.click();
      }

      pick(kind, id) {
        if (this.state[kind] === id) { this.afterPick(kind); return; }
        this.state[kind] = id;
        this.render(true);
        this.afterPick(kind);
      }

      afterPick(kind) {
        /* A phone's question list sits above its answer: take the visitor to it. */
        if (kind === 'topic' && isMobile()) setTimeout(() => this.scrollTo('answer'), 40);
      }

      scrollTo(name) {
        const el = this.querySelector(`[data-sd-anchor="${name}"]`);
        if (el) el.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'start' });
      }

      render(user) {
        const { model, topic } = this.state;
        const mark = (list, attr, id) => list.forEach((el) => {
          const on = el.dataset[attr] === id;
          el.setAttribute('aria-checked', on ? 'true' : 'false');
          el.tabIndex = on ? 0 : -1;
        });
        mark(this.models, 'sdModel', model);
        mark(this.topics, 'sdTopic', topic);
        this.dataset.model = model;
        this.dataset.topic = topic;

        const variant = this.variants[topic] || 'a';
        let shown = null;
        this.panels.forEach((p) => {
          const rightTopic = p.dataset.topic === topic;
          const rightModel = p.dataset.model === '*' || p.dataset.model === model;
          const rightVariant = !p.dataset.variant || p.dataset.variant === variant;
          const on = rightTopic && rightModel && rightVariant;
          if (on) shown = p;
          if (p.hidden !== !on) p.hidden = !on;
          if (!on) this.stop(p);
        });

        if (user) {
          this.url();
          this.announce(shown);
        }
      }

      /* Keep the address naming what is on screen. Never a navigation, never in the theme editor. A link to one question
         keeps its #faq- anchor (keepHash), so the address still opens that question. */
      url(keepHash) {
        if (window.Shopify && window.Shopify.designMode) return;
        try {
          const u = new URL(window.location.href);
          u.searchParams.set('topic', this.state.topic);
          u.searchParams.set('model', this.state.model);
          if (!keepHash) u.hash = '';
          window.history.replaceState(null, '', u);
        } catch (err) { /* an unusual address: leave it */ }
      }

      announce(panel) {
        if (!this.live || !panel) return;
        const topicBtn = this.topics.find((x) => x.dataset.sdTopic === this.state.topic);
        const modelBtn = this.models.find((x) => x.dataset.sdModel === this.state.model);
        const perModel = panel.dataset.model !== '*';
        const text = perModel ? this.dataset.statusModel : this.dataset.status;
        this.live.textContent = fillTokens(text, {
          topic: topicBtn ? topicBtn.dataset.sdLabel : '',
          model: modelBtn ? (modelBtn.querySelector('.o-choice__label') || modelBtn).textContent.trim() : ''
        });
      }

      /* A setup video: still → no-cookie player on a press, captions on. */
      play(btn) {
        const id = btn.getAttribute('data-o-vf-play');
        const frame = btn.closest('[data-o-vf]');
        if (!id || !frame) return;
        frame._restore = frame.innerHTML;
        const iframe = document.createElement('iframe');
        iframe.src = player(id);
        iframe.title = (btn.getAttribute('aria-label') || 'Video').replace(/^Play:\s*/, '');
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        frame.innerHTML = '';
        frame.appendChild(iframe);
        iframe.focus();
      }

      stop(panel) {
        panel.querySelectorAll('[data-o-vf]').forEach((frame) => {
          if (frame._restore != null && frame.querySelector('iframe')) { frame.innerHTML = frame._restore; frame._restore = null; }
        });
      }
    }
    customElements.define('oricle-support-desk', OricleSupportDesk);
  }

  /* —— Contact form (sections/oricle-support-contact.liquid) —— */
  if (!customElements.get('oricle-support-contact')) {
    class OricleSupportContact extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.form = this.querySelector('[data-sc-form]');
        this.sent = this.querySelector('[data-sc-sent]');
        this.err = this.querySelector('[data-sc-error]');
        this.submitBtn = this.querySelector('[data-sc-submit]');
        if (!this.form) return;
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
        const again = this.querySelector('[data-sc-again]');
        if (again) again.addEventListener('click', (e) => { e.preventDefault(); this.reset(); });
      }

      /* The topic on screen goes in the subject, so support sees what the visitor was reading. */
      topicLabel() {
        const desk = document.querySelector('oricle-support-desk');
        const btn = desk && desk.querySelector('[data-sd-topic][aria-checked="true"]');
        return btn ? btn.dataset.sdLabel : '';
      }

      body(data) { return data.message + (data.order ? `\n\nOrder: ${data.order}` : ''); }

      onSubmit(e) {
        e.preventDefault();
        const fd = new FormData(this.form);
        const data = { name: String(fd.get('name') || ''), email: String(fd.get('email') || ''), order: String(fd.get('order') || ''), message: String(fd.get('message') || '') };
        const subject = fillTokens(this.dataset.subject, { topic: this.topicLabel() });
        const sub = (this.dataset.zendesk || '').trim();
        if (this.err) this.err.hidden = true;
        if (!sub) {
          /* No ticket desk is connected: open the visitor's email app, addressed and filled in. Not "sent". */
          this.open(`mailto:${this.dataset.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(this.body(data))}`);
          return;
        }
        if (this.submitBtn) { this.submitBtn.disabled = true; this.submitBtn.textContent = this.dataset.sending; }
        fetch(`https://${encodeURIComponent(sub)}.zendesk.com/api/v2/requests.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ request: { requester: { name: data.name || data.email, email: data.email }, subject, comment: { body: this.body(data) } } })
        }).then((r) => { if (r.ok) this.done(data.email); else this.fail(); }).catch(() => this.fail());
      }

      /* The one place the page navigates away: a method so a test can observe the address instead of leaving. */
      open(url) { window.location.href = url; }

      done(to) {
        this.form.hidden = true;
        if (this.sent) {
          const note = this.sent.querySelector('[data-sc-sent-sub]');
          if (note) note.textContent = fillTokens(this.dataset.sentSub, { to: to || this.dataset.email });
          this.sent.hidden = false;
        }
      }

      fail() {
        if (this.submitBtn) { this.submitBtn.disabled = false; this.submitBtn.textContent = this.dataset.send; }
        if (this.err) this.err.hidden = false;
      }

      reset() {
        this.form.reset();
        this.form.hidden = false;
        if (this.sent) this.sent.hidden = true;
        if (this.submitBtn) { this.submitBtn.disabled = false; this.submitBtn.textContent = this.dataset.send; }
      }
    }
    customElements.define('oricle-support-contact', OricleSupportContact);
  }
})();
